import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { FileText, Upload, Trash2, Download } from "lucide-react";
import { toast } from "sonner";

const fmtSize = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function DocumentsTab({ projectId, readOnly }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/projects/${projectId}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", form.title);
    fd.append("notes", form.notes);
    try {
      await api.post(`/projects/${projectId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setOpen(false);
      setForm({ title: "", notes: "" });
      setFile(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = d.filename || d.title || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not download file");
    }
  };

  const remove = async (id) => { await api.delete(`/documents/${id}`); load(); };

  return (
    <div className="max-w-4xl space-y-6" data-testid="documents-tab">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Project Files</p>
          <h3 className="font-serif text-3xl text-[#2B2622]">Documents</h3>
        </div>
        {!readOnly && (
          <Button data-testid="new-document-btn" onClick={() => setOpen(true)}
            className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5 px-5">
            <Upload size={15} className="mr-2" /> Upload Document
          </Button>
        )}
      </div>

      {docs.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-12 text-center text-[#5C5651]">
          No documents uploaded yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-px bg-[#EBE5E0] border border-[#EBE5E0]">
          {docs.map((d) => (
            <div key={d.id} className="bg-white p-6 flex flex-col" data-testid="document-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 bg-[#F2EFEB] flex items-center justify-center shrink-0">
                    <FileText size={18} strokeWidth={1.5} className="text-[#A68A6B]" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-serif text-lg text-[#2B2622] truncate">{d.title}</h4>
                    <p className="text-[11px] text-[#9b938b] truncate">{d.filename} · {fmtSize(d.size)}</p>
                  </div>
                </div>
                {!readOnly && (
                  <button data-testid={`delete-document-${d.id}`} onClick={() => remove(d.id)}
                    className="text-[#5C5651] hover:text-[#9E4747] shrink-0"><Trash2 size={15} strokeWidth={1.5} /></button>
                )}
              </div>
              {d.notes && <p className="text-sm text-[#5C5651] mt-3 leading-relaxed">{d.notes}</p>}
              <div className="mt-4 pt-4 border-t border-[#F2EFEB] flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-[#A68A6B]">{d.uploaded_by}</span>
                <button data-testid={`download-document-${d.id}`} onClick={() => download(d)}
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#2B2622] hover:text-[#A68A6B]">
                  <Download size={14} strokeWidth={1.5} /> Download
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#2B2622]">Upload document</DialogTitle><DialogDescription className="sr-only">Upload a project document</DialogDescription></DialogHeader>
          <form onSubmit={upload} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Title</Label>
              <Input data-testid="document-title-input" required value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Notes</Label>
              <Textarea data-testid="document-notes-input" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">File</Label>
              <input ref={fileRef} data-testid="document-file-input" type="file"
                onChange={(e) => setFile(e.target.files[0])}
                className="mt-1 block w-full text-sm text-[#5C5651] file:mr-3 file:border file:border-[#A68A6B] file:bg-transparent file:text-[#A68A6B] file:px-4 file:py-2 file:uppercase file:text-xs file:tracking-wider" />
            </div>
            <Button data-testid="upload-document-submit" type="submit" disabled={busy}
              className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
              {busy ? "Uploading…" : "Upload"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
